import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

const ROWS = [
  { nameKey: "accounts.tiers.r1.name", depositKey: "accounts.tiers.r1.deposit", marketKey: "accounts.tiers.r1.market", featuresKey: "accounts.tiers.r1.features", bestKey: "accounts.tiers.r1.best" },
  { nameKey: "accounts.tiers.r2.name", depositKey: "accounts.tiers.r2.deposit", marketKey: "accounts.tiers.r2.market", featuresKey: "accounts.tiers.r2.features", bestKey: "accounts.tiers.r2.best" },
  { nameKey: "accounts.tiers.r3.name", depositKey: "accounts.tiers.r3.deposit", marketKey: "accounts.tiers.r3.market", featuresKey: "accounts.tiers.r3.features", bestKey: "accounts.tiers.r3.best" },
  { nameKey: "accounts.tiers.r4.name", depositKey: "accounts.tiers.r4.deposit", marketKey: "accounts.tiers.r4.market", featuresKey: "accounts.tiers.r4.features", bestKey: "accounts.tiers.r4.best" },
];

function MarketBadge({ children }) {
  return (
    <span className="inline-flex bg-[#fcefea] rounded-[100px] px-[14px] py-[5px] font-['Inter'] font-medium text-[12px] text-[#ee5414] leading-normal whitespace-nowrap">
      {children}
    </span>
  );
}

const MOBILE_COLUMNS = [
  {
    titleKey: "accounts.tiers.h.deposit",
    render: (row, t) => (
      <span className="font-['Montserrat'] font-extrabold text-[14px] text-[#020202] leading-normal">{t(row.depositKey)}</span>
    ),
  },
  {
    titleKey: "accounts.tiers.h.market",
    render: (row, t) => <MarketBadge>{t(row.marketKey)}</MarketBadge>,
  },
  {
    titleKey: "accounts.tiers.h.features",
    render: (row, t) => (
      <span className="font-['Inter'] font-normal text-[13px] text-[#888] leading-normal">{t(row.featuresKey)}</span>
    ),
  },
  {
    titleKey: "accounts.tiers.h.best",
    render: (row, t) => (
      <span className="font-['Inter'] font-normal text-[13px] text-[#888] leading-normal">{t(row.bestKey)}</span>
    ),
  },
];

const ROW_BG = (i) => (i % 2 === 0 ? "border-b border-[#ebebeb]" : "bg-[#f7f7f7]");

function Tiers() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [activeColumn, setActiveColumn] = useState(0);

  return (
    <section className="mt">
      <div className="glcontainer flex flex-col gap-[40px] xl:gap-[60px]">

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-[20px]">
          <div className="flex flex-col">
            <span className="font-['Montserrat'] font-black text-[16px] xl:text-[20px] text-[#0a0a0a] tracking-[-0.92px] uppercase leading-none">
              {t("accounts.tiers.subtitle")}
            </span>
            <h2 className="font-['Montserrat'] font-black text-[24px] xl:text-[46px] text-[#0a0a0a] tracking-[-0.92px] uppercase leading-none">
              {t("accounts.tiers.title")}
            </h2>
          </div>
          <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#717171] leading-[1.4] xl:max-w-[600px]">
            {t("accounts.tiers.desc")}
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden xl:flex flex-col pt-[16px]">
          <div className="grid grid-cols-[200px_204px_258px_258px_1fr] border-b-2 border-[#0a0a0a] pb-[19px]">
            <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase pl-[32px] pr-[16px]">{t("accounts.tiers.h.account")}</span>
            <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase px-[16px]">{t("accounts.tiers.h.deposit")}</span>
            <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase px-[16px]">{t("accounts.tiers.h.market")}</span>
            <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase px-[16px]">{t("accounts.tiers.h.features")}</span>
            <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase pl-[16px] pr-[32px] text-right">{t("accounts.tiers.h.best")}</span>
          </div>
          {ROWS.map((row, i) => (
            <div key={i} className={`grid grid-cols-[200px_204px_258px_258px_1fr] items-center py-[27px] ${ROW_BG(i)}`}>
              <span className="font-['Montserrat'] font-extrabold text-[17px] text-[#020202] tracking-[-0.17px] uppercase leading-normal pl-[32px] pr-[16px]">{t(row.nameKey)}</span>
              <span className="font-['Montserrat'] font-extrabold text-[17px] text-[#020202] leading-normal pl-[32px] pr-[16px]">{t(row.depositKey)}</span>
              <div className="px-[16px]"><MarketBadge>{t(row.marketKey)}</MarketBadge></div>
              <span className="font-['Inter'] font-normal text-[14px] text-[#888] leading-normal px-[16px]">{t(row.featuresKey)}</span>
              <span className="font-['Inter'] font-normal text-[14px] text-[#888] leading-normal pl-[16px] pr-[32px] text-right">{t(row.bestKey)}</span>
            </div>
          ))}
        </div>

        {/* Mobile table: fixed Account column + swipeable data column */}
        <div className="xl:hidden flex pt-[16px] min-w-0">

          {/* Left: Account column */}
          <div className="w-[120px] shrink-0 flex flex-col">
            <div className="h-[34px] flex items-start border-b-2 border-[#0a0a0a]">
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase">{t("accounts.tiers.h.account")}</span>
            </div>
            {ROWS.map((row, i) => (
              <div key={i} className={`h-[60px] flex items-center ${ROW_BG(i)}`}>
                <span className="font-['Montserrat'] font-extrabold text-[14px] text-[#020202] tracking-[-0.17px] uppercase leading-normal">{t(row.nameKey)}</span>
              </div>
            ))}
          </div>

          {/* Right: swipeable column */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="h-[34px] flex items-start justify-between gap-[8px] border-b-2 border-[#0a0a0a]">
              <button
                type="button"
                onClick={() => swiperRef.current?.slidePrev()}
                disabled={activeColumn === 0}
                className="flex items-center justify-center w-[20px] h-[16px] shrink-0 disabled:opacity-30"
                aria-label="Previous column"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M8.19092 0.494953L0.989907 7.69596L8.19092 14.897M0.989907 7.69596L15.3919 7.69596" stroke="#707070" strokeWidth="1.4" />
                </svg>
              </button>
              <span className="font-['Montserrat'] font-bold text-[11px] text-[#999] tracking-[1.32px] uppercase whitespace-nowrap overflow-hidden text-ellipsis">
                {t(MOBILE_COLUMNS[activeColumn].titleKey)}
              </span>
              <button
                type="button"
                onClick={() => swiperRef.current?.slideNext()}
                disabled={activeColumn === MOBILE_COLUMNS.length - 1}
                className="flex items-center justify-center w-[20px] h-[16px] shrink-0 disabled:opacity-30"
                aria-label="Next column"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M7.20117 14.8972L14.4023 7.69609L7.20117 0.494995M14.4023 7.69609H8.10744e-05" stroke="#707070" strokeWidth="1.4" />
                </svg>
              </button>
            </div>

            <Swiper
              slidesPerView={1}
              spaceBetween={0}
              onSwiper={(swiper) => { swiperRef.current = swiper; }}
              onSlideChange={(swiper) => setActiveColumn(swiper.activeIndex)}
              className="w-full min-w-0 overflow-hidden"
            >
              {MOBILE_COLUMNS.map((column, c) => (
                <SwiperSlide key={c}>
                  <div className="flex flex-col">
                    {ROWS.map((row, i) => (
                      <div key={i} className={`h-[60px] flex items-center justify-center px-[8px] overflow-hidden ${ROW_BG(i)}`}>
                        {column.render(row, t)}
                      </div>
                    ))}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

        </div>

      </div>
    </section>
  );
}

export default Tiers;
